// Plays the slice of a recording a word came from, by seeking an <audio>
// element on the blob rather than decoding it: MediaRecorder's containers
// differ per browser (webm/opus, mp4/aac) and `decodeAudioData` does not
// take all of them everywhere, while every browser plays back what it
// recorded. Seeking a MediaRecorder webm is known to be approximate in
// Chromium (no cues index; from memory, unverified), so the slice is padded.
const PAD_MS = 120;
// A blob that never reports metadata (nor an error) would otherwise hold its
// object URL for the page's lifetime.
const LOAD_DEADLINE_MS = 8000;

/** "done" when the slice played (or could not), "cancelled" when the page cut it off. */
export type ClipOutcome = "done" | "cancelled";

export interface ClipPlayback {
  readonly done: Promise<ClipOutcome>;
  readonly cancel: () => void;
}

export function playClip(
  blob: Blob,
  startMs: number,
  endMs: number,
): ClipPlayback {
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  const from = Math.max(0, startMs - PAD_MS) / 1000;
  const lengthMs = Math.max(250, endMs - startMs + 2 * PAD_MS);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let finished = false;
  let finish: (outcome: ClipOutcome) => void = () => undefined;

  const done = new Promise<ClipOutcome>((resolve) => {
    finish = (outcome) => {
      if (finished) return;
      finished = true;
      if (timer !== null) clearTimeout(timer);
      audio.pause();
      audio.removeAttribute("src");
      URL.revokeObjectURL(url);
      resolve(outcome);
    };
    const played = () => finish("done");
    const begin = () => {
      if (finished) return;
      if (timer !== null) clearTimeout(timer);
      audio.currentTime = from;
      void audio.play().catch(played);
      timer = setTimeout(played, lengthMs);
    };
    audio.addEventListener("loadedmetadata", begin, { once: true });
    audio.addEventListener("ended", played, { once: true });
    audio.addEventListener("error", played, { once: true });
    timer = setTimeout(played, LOAD_DEADLINE_MS);
    audio.load();
  });

  return { done, cancel: () => finish("cancelled") };
}
